import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiMinus, FiPlus, FiRefreshCw } from "react-icons/fi";
import PopupCard from "./PopupCard";

export default function Counter() {
  const navigate = useNavigate();
  const [amount, setAmount] = useState(25);
  const [coinsInput, setCoinsInput] = useState("");
  const [isSwapped, setIsSwapped] = useState(false);
  const COINS_PER_USD = 2000; // 1 USD = 2000 coins

  const handleDecrease = () => {
    if (isSwapped) {
      const currentCoins = coinsInput !== "" && !isNaN(parseFloat(coinsInput)) ? parseFloat(coinsInput) : (amount && !isNaN(amount) && amount >= 0 ? amount : 25) * COINS_PER_USD;
      const newCoins = Math.max(COINS_PER_USD * 25, currentCoins - (COINS_PER_USD * 25));
      setCoinsInput(newCoins.toString());
      setAmount(newCoins / COINS_PER_USD);
    } else {
      setAmount((prev) => Math.max(25, prev - 25));
    }
  };

  const handleIncrease = () => {
    if (isSwapped) {
      const currentCoins = coinsInput !== "" && !isNaN(parseFloat(coinsInput)) ? parseFloat(coinsInput) : (amount && !isNaN(amount) && amount >= 0 ? amount : 25) * COINS_PER_USD;
      const newCoins = currentCoins + (COINS_PER_USD * 25);
      setCoinsInput(newCoins.toString());
      setAmount(newCoins / COINS_PER_USD);
    } else {
      setAmount((prev) => prev + 25);
    }
  };

  const handleRefreshClick = () => {
    setIsSwapped((prev) => {
      if (!prev) {
        // When swapping to coins, initialize coinsInput
        const currentAmount = amount && !isNaN(amount) && amount >= 0 ? amount : 25;
        setCoinsInput((currentAmount * COINS_PER_USD).toLocaleString());
      } else {
        // When swapping back to USD, clear coinsInput
        setCoinsInput("");
      }
      return !prev;
    });
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    // Allow empty string for typing
    if (value === "") {
      setAmount("");
      return;
    }
    // Only allow numbers
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      setAmount(numValue);
    }
  };

  const handleAmountBlur = () => {
    // Ensure minimum value of 25 when user leaves the field
    if (amount === "" || amount < 25) {
      setAmount(25);
      setCoinsInput("");
    }
  };

  const handleCoinsChange = (e) => {
    const value = e.target.value.replace(/,/g, ""); // Remove commas
    // Allow empty string for typing
    if (value === "") {
      setCoinsInput("");
      return;
    }
    // Only allow numbers
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      setCoinsInput(value);
      setAmount(numValue / COINS_PER_USD);
    }
  };

  const handleCoinsBlur = () => {
    // Ensure minimum value when user leaves the field
    const currentCoins = coinsInput !== "" && !isNaN(parseFloat(coinsInput)) ? parseFloat(coinsInput) : 0;
    const minCoins = COINS_PER_USD * 25;
    if (currentCoins < minCoins) {
      setCoinsInput(minCoins.toString());
      setAmount(25);
    } else {
      // Format with commas
      setCoinsInput(currentCoins.toLocaleString());
    }
  };

  const handleBuyNow = () => {
    // Ensure minimum amount before navigating
    const finalAmount = amount && !isNaN(amount) && amount >= 25 ? amount : 25;
    // Navigate to checkout or packages page with the selected amount
    navigate("/checkout", {
      state: {
        package: {
          type: "custom",
          title: "Buy SynergyCoin",
          price: `${finalAmount} USD`,
          amount: finalAmount,
          coins: finalAmount * COINS_PER_USD,
        },
      },
    });
  };

  const handleSkip = () => {
    navigate(-1);
  };

  const coins = (amount && !isNaN(amount) && amount >= 0 ? amount : 25) * COINS_PER_USD;

  return (
    <div className="relative h-screen w-full bg-black overflow-hidden flex flex-col">
      <PopupCard />
      {/* Background Images */}
      <img
        src="/images/leftcorner.png"
        alt="Left Corner"
        className="fixed bottom-0 left-0 z-0 opacity-80 w-32 sm:w-48 md:w-56 lg:w-64 pointer-events-none"
      />
      <img
        src="/images/right-corner.png"
        alt="Right Corner"
        className="fixed bottom-0 right-0 z-0 opacity-80 w-32 sm:w-48 md:w-56 lg:w-64 pointer-events-none"
      />

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-4 py-4 sm:px-6 sm:py-6 flex-shrink-0">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 sm:gap-3 cursor-pointer hover:opacity-80 transition-opacity"
        >
          <img
            src="/images/logo.png"
            alt="SynergyCoin"
            className="h-6 w-auto sm:h-8 md:h-10"
          />
          <span className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-[#ECEFF0] to-[#E5C0FF] bg-clip-text text-transparent">
            SynergyCoin
          </span>
        </button>
        <button
          onClick={handleSkip}
          className="text-xs sm:text-sm md:text-base text-white/70 hover:text-white transition-colors"
        >
          Skip
        </button>
      </header>

      {/* Main Content */}
      <div className="relative z-10 flex flex-col items-center justify-center flex-1 px-4 sm:px-6 overflow-y-auto">
        <div className="w-full max-w-md lg:max-w-lg xl:max-w-xl mx-auto py-4">
          {/* Title */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white text-center mb-2 sm:mb-3 md:mb-4">
            Buy SynergyCoin
          </h1>
          <p className="text-xs sm:text-sm md:text-base text-white/70 text-center mb-4 sm:mb-6 md:mb-8">
            Enter amount you want to pay
          </p>

          {/* Central Card */}
          <div className="w-full rounded-2xl sm:rounded-3xl border border-white/10 bg-gradient-to-b from-[#1A1B24]/90 via-[#12131A]/95 to-[#0B0C10] p-4 sm:p-6 md:p-8 lg:p-10 shadow-2xl">
            {/* Amount Input / Coins Display (swappable) */}
            <div className="flex items-center justify-between rounded-xl sm:rounded-2xl border border-[#7A3EE6]/70 bg-gradient-to-r from-[#2B124F] via-[#2D1A69] to-[#11205C] px-3 sm:px-4 md:px-5 py-3 sm:py-4 md:py-5">
              <button
                type="button"
                onClick={handleDecrease}
                className="flex h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 items-center justify-center rounded-lg bg-[#8C3CDD]/30 border border-[#A86BFF]/40 text-white transition hover:bg-[#8C3CDD]/50 hover:scale-105 active:scale-95 flex-shrink-0"
                aria-label="Decrease amount"
              >
                <FiMinus className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" />
              </button>
              {isSwapped ? (
                <div className="flex-1 flex items-center justify-center gap-1 sm:gap-1.5">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={coinsInput !== "" ? coinsInput : coins.toLocaleString()}
                    onChange={handleCoinsChange}
                    onBlur={handleCoinsBlur}
                    onFocus={(e) => {
                      // Remove commas when focused for easier editing
                      const value = e.target.value.replace(/,/g, "");
                      setCoinsInput(value);
                    }}
                    className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-white text-center bg-transparent border-none outline-none focus:outline-none max-w-[150px] sm:max-w-[200px] md:max-w-[250px] leading-none"
                    style={{ WebkitAppearance: "none", MozAppearance: "textfield" }}
                  />
                  <span className="flex h-6 w-6 sm:h-8 sm:w-8 md:h-10 md:w-10 items-center justify-center flex-shrink-0">
                    <img
                      src="/images/logo.png"
                      alt="Synergy Coin"
                      className="h-6 w-6 sm:h-8 sm:w-8 md:h-10 md:w-10 object-contain"
                    />
                  </span>
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center gap-1 sm:gap-1.5">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={amount}
                    onChange={handleAmountChange}
                    onBlur={handleAmountBlur}
                    className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white text-center bg-transparent border-none outline-none focus:outline-none max-w-[120px] sm:max-w-[150px] md:max-w-[180px]"
                    style={{ WebkitAppearance: "none", MozAppearance: "textfield" }}
                  />
                  <span className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white">
                    USD
                  </span>
                </div>
              )}
              <button
                type="button"
                onClick={handleIncrease}
                className="flex h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 items-center justify-center rounded-lg bg-[#8C3CDD]/30 border border-[#A86BFF]/40 text-white transition hover:bg-[#8C3CDD]/50 hover:scale-105 active:scale-95 flex-shrink-0"
                aria-label="Increase amount"
              >
                <FiPlus className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" />
              </button>
            </div>

            {/* Conversion Display / Amount Display (swappable) */}
            <div className="mt-4 sm:mt-6 flex items-center justify-center gap-2 sm:gap-3 text-white/80">
              <button
                type="button"
                onClick={handleRefreshClick}
                className="cursor-pointer transition-transform hover:rotate-180 duration-300"
              >
                <FiRefreshCw className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-[#C9A7FF]" />
              </button>
              <span className="text-base sm:text-lg md:text-xl">=</span>
              {isSwapped ? (
                <div className="flex items-center justify-center gap-1 sm:gap-1.5">
                  <span className="text-xl sm:text-2xl md:text-3xl font-bold text-white">
                    {amount && !isNaN(amount) && amount >= 0 ? amount.toFixed(2) : "25.00"}
                  </span>
                  <span className="text-xl sm:text-2xl md:text-3xl font-bold text-white">
                    USD
                  </span>
                </div>
              ) : (
                <>
                  <span className="text-xl sm:text-2xl md:text-3xl font-semibold text-white leading-none">
                    {coins.toLocaleString()}
                  </span>
                  <span className="flex h-6 w-6 sm:h-8 sm:w-8 md:h-10 md:w-10 items-center justify-center flex-shrink-0">
                    <img
                      src="/images/logo.png"
                      alt="Synergy Coin"
                      className="h-6 w-6 sm:h-8 sm:w-8 md:h-10 md:w-10 object-contain"
                    />
                  </span>
                </>
              )}
            </div>

            {/* Buy Now Button */}
            <button
              onClick={handleBuyNow}
              className="mt-6 sm:mt-8 w-full rounded-lg sm:rounded-xl bg-gradient-to-r from-[#8C3CDD] to-[#A367FF] py-3 sm:py-4 text-sm sm:text-base md:text-lg font-semibold text-white shadow-[0_8px_24px_rgba(140,60,221,0.4)] transition hover:shadow-[0_12px_32px_rgba(140,60,221,0.6)] hover:scale-[1.02] active:scale-[0.98]"
            >
              Buy Now
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 text-center px-4 py-3 sm:py-4 md:py-6 flex-shrink-0">
        <p className="text-[10px] sm:text-xs md:text-sm text-white/50">
          By using SynergyCoin you agree to our terms and policy.
        </p>
      </footer>
    </div>
  );
}
